import * as React from 'react';
import {
    Box,
    Stack,
    useDialog,
    IconTrashCanRegular,
    ResponsiveLayout,
    GridLayout,
    Logo,
    Title4,
    BoxedRowList,
    BoxedRow,
    IconUserAccountRegular,
    skinVars,
} from '@telefonica/mistica';
import {getAccounts, logout, getAuthParams} from '../../api';
import EmptyCase from './empty-case';
import {I18N} from '../../utils/i18n';
import {trackEvent, setScreenName} from '../../utils/analytics';
import LoadingScreen from '../loading-screen';
import {useDocumentTitle} from '../../hooks';
import {useBrand, useCompanyName} from '../app-context-provider';

import type {UserAccount} from '../../api';
import type {Brand} from '../../utils/brands';

const AccountManagement = (): JSX.Element => {
    const {confirm} = useDialog();
    const [userAccounts, setUserAccounts] = React.useState<Array<UserAccount>>([]);
    const [isLoading, setLoading] = React.useState(true);
    const [showEmptyCase, setShowEmptyCase] = React.useState(false);
    const {acrValues, clientId = ''} = getAuthParams();
    const brand = useBrand();
    const companyName = useCompanyName();
    useDocumentTitle(I18N.translate('Gestionar usuarios'));

    const getUserAccounts = (brand: Brand, clientId: string, acrValues: Array<string>): void => {
        getAccounts(brand, clientId, acrValues)
            .then((result) => {
                setUserAccounts(result);
                if (!result || !result.length) {
                    setShowEmptyCase(true);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    React.useEffect(() => {
        if (showEmptyCase) {
            setScreenName('login_account_chooser_management/empty_case');
        } else {
            setScreenName('login_account_chooser_management');
        }
    }, [showEmptyCase]);

    React.useEffect(() => {
        getUserAccounts(brand, clientId, acrValues);
    }, [brand, clientId, acrValues]);

    const onUserLogout = (ssoToken: string, identifier: string): void => {
        setLoading(true);
        logout(ssoToken)
            .then(() => {
                getUserAccounts(brand, clientId, acrValues);
            })
            .catch(() => {
                const title = I18N.translate('Algo ha ido mal');
                const errorAcceptTextTranslated = I18N.translate('Reintentar');
                const errorCancelTextTranslated = I18N.translate('Cancelar');
                trackEvent({name: 'popup_view', popup_title: title});
                confirm({
                    title,
                    message: I18N.translate('No se ha podido eliminar el usuario 1$s', identifier),
                    acceptText: errorAcceptTextTranslated,
                    cancelText: errorCancelTextTranslated,
                    onAccept: () => {
                        onUserLogout(ssoToken, identifier);
                        trackEvent({
                            name: 'popup_interaction',
                            popup_title: title,
                            popup_option: errorAcceptTextTranslated,
                        });
                    },
                    onCancel: () => {
                        setLoading(false);
                        trackEvent({
                            name: 'popup_interaction',
                            popup_title: title,
                            popup_option: errorCancelTextTranslated,
                        });
                    },
                });
            });
    };

    const handleRemove = (ssoToken: string, identifier: string): void => {
        const title = I18N.translate('¿Seguro que quieres eliminar este usuario?');
        const confirmAcceptTextTranslated = I18N.translate('Eliminar');
        const cancelTextTranslated = I18N.translate('Cancelar');
        trackEvent({name: 'popup_view', popup_title: title});
        confirm({
            title,
            message: I18N.translate(
                'Tendrás que volver a iniciar sesión con 1$s la proxima vez que uses una app de 2$s',
                identifier,
                companyName
            ),
            acceptText: confirmAcceptTextTranslated,
            cancelText: cancelTextTranslated,
            onAccept: () => {
                trackEvent({
                    name: 'popup_interaction',
                    popup_title: title,
                    popup_option: confirmAcceptTextTranslated,
                });
                onUserLogout(ssoToken, identifier);
            },
            onCancel: () => {
                trackEvent({
                    name: 'popup_interaction',
                    popup_title: title,
                    popup_option: cancelTextTranslated,
                });
            },
        });
    };

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (showEmptyCase) {
        return <EmptyCase />;
    }

    return (
        <ResponsiveLayout>
            <GridLayout
                template="6+6"
                right={null}
                left={
                    <Box paddingTop={32} paddingBottom={24}>
                        <Stack space={32}>
                            <Stack space={24}>
                                <Logo size={{mobile: 40, desktop: 48}} />
                                <Title4 as="h1">{I18N.translate('Selecciona una cuenta')}</Title4>
                            </Stack>
                            <BoxedRowList>
                                {userAccounts.map((user) => (
                                    <BoxedRow
                                        asset={<IconUserAccountRegular color={skinVars.colors.brand} />}
                                        key={user.ssoToken}
                                        {...(user.name
                                            ? {
                                                  title: user.name,
                                                  description: user.identifier,
                                              }
                                            : {
                                                  title: user.identifier,
                                              })}
                                        iconButton={{
                                            'aria-label': I18N.translate('Eliminar usuario'),
                                            trackingEvent: {
                                                name: 'user_interaction',
                                                component_type: 'icon',
                                                component_copy: I18N.translate('Eliminar usuario'),
                                            },
                                            onPress: () => {
                                                handleRemove(user.ssoToken, user.identifier);
                                            },
                                            Icon: IconTrashCanRegular,
                                            type: 'danger',
                                        }}
                                    />
                                ))}
                            </BoxedRowList>
                        </Stack>
                    </Box>
                }
            />
        </ResponsiveLayout>
    );
};

export default AccountManagement;
